<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"

const operationStore = useOperationStore()

const schema = yup.object().shape({
  result: yup.string().required("Pflichtfeld"),
  shipped: yup.boolean().test(
    "is-checked",
    "Der Versand muss bestätigt werden",
    yupTestOnlyOnCreate((value) => value === true)
  ),
  bqfgRelevance: yup.boolean(),
  caseCompletionDate: yup.string().when("shipped", {
  is: true,
  then: (s) => s.required("Pflichtfeld"),
  otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

watch(
    () => state.shipped,
    (value) => {
      if (value === false) {
        state.caseCompletionDate = undefined
      }
    }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Verfahrensabschluss"
      />
    </template>

    <IffContentSection headline="Art des Verfahrensabschlusses">
      <IffRadioGroup
        legend="Ergebnis"
        name="result"
        v-model="state.result"
        :options="[
          {
            label: 'Offen',
            value: 'open',
          },
          {
            label: 'Antragsrücknahme',
            value: 'withdrawn',
          },
          {
            label: 'Ablehnungsbescheid',
            value: 'rejected',
          },
        ]"
        required
      />

      <div class="flex items-center gap-2">
        <UCheckbox label="BQFG-Relevanz" v-model="state.bqfgRelevance">
        </UCheckbox>
        <IffIconTooltip
          text='Setzt "Entscheidung vor Rechtsbehelf" e09 auf "Sonstige Erledigungsarten – Verfahren ohne Bescheid beendet"'
        />
      </div>
    </IffContentSection>
    <IffContentSection headline="Bestätigen" :is-last-child="true">
      <UFormGroup name="shipped">
        <UCheckbox
          required
          label="Ich bestätige den Versand der erforderlichen Dokumente an die antragstellende Person."
          v-model="state.shipped"
          aria-required="true"
        />
      </UFormGroup>
      <div id="case-completion-date-region" aria-live="polite">
        <IffTransitionHeight>
        <UFormGroup
            name="caseCompletionDate"
            label="Datum Verfahrensabschluss"
            required
            v-if="state.shipped"
            class="max-w-[240px]"
        >
          <template #hint>
            <UButton
                @click="
                    state.caseCompletionDate = new Date()
                      .toISOString()
                      .slice(0, 10)
                  "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
            />
          </template>

          <UInput
              v-model="state.caseCompletionDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
              required
          />
        </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
